import { supabase } from '../../lib/supabaseClient';
import { ShippingMethod, DeliveryAreaInfo, postalFsaPrefix } from '../../lib/storefront';

/** The online store's delivery setup, as staff-side order tooling needs it. */
export interface StoreShippingConfig {
  shippingMethods: ShippingMethod[];
  enableInStorePickup: boolean;
  pickupAddress: string;
  enforced: boolean;
  enforcedPrefixes: string[];
  areas: DeliveryAreaInfo[];
}

export const FALLBACK_SHIPPING_CONFIG: StoreShippingConfig = {
  shippingMethods: [],
  enableInStorePickup: true,
  pickupAddress: '',
  enforced: false,
  enforcedPrefixes: [],
  areas: [],
};

/**
 * Load the same shipping methods / pickup / delivery-area setup the online
 * checkout uses, resolved for staff-side pricing (area rates need prefixes).
 */
export async function loadStoreShippingConfig(businessId: string): Promise<StoreShippingConfig> {
  const { data: settings } = await supabase
    .from('online_store_settings')
    .select('shipping_methods, enable_in_store_pickup, pickup_address, enforce_delivery_areas, delivery_area_ids')
    .eq('business_id', businessId)
    .maybeSingle();
  if (!settings) return FALLBACK_SHIPPING_CONFIG;

  const methods: ShippingMethod[] = Array.isArray(settings.shipping_methods) ? settings.shipping_methods : [];
  const enforcedIds: string[] = settings.enforce_delivery_areas === true ? (settings.delivery_area_ids || []) : [];
  const ratedIds = methods
    .flatMap(m => (Array.isArray(m?.areaRates) ? m.areaRates.map(r => String(r?.areaId || '')) : []))
    .filter(Boolean);
  const allIds = [...new Set([...enforcedIds, ...ratedIds])];

  const areas: DeliveryAreaInfo[] = [];
  const enforcedPrefixes = new Set<string>();
  if (allIds.length > 0) {
    const { data: areaRows } = await supabase
      .from('service_areas')
      .select('id, name, postal_codes')
      .eq('business_id', businessId)
      .in('id', allIds);
    for (const row of areaRows || []) {
      const prefixes = new Set<string>();
      for (const code of row.postal_codes || []) {
        const p = postalFsaPrefix(String(code || ''));
        if (p.length === 3) prefixes.add(p);
      }
      areas.push({ id: row.id, name: row.name, prefixes: [...prefixes].sort() });
      if (enforcedIds.includes(row.id)) for (const p of prefixes) enforcedPrefixes.add(p);
    }
  }

  return {
    shippingMethods: methods,
    enableInStorePickup: settings.enable_in_store_pickup ?? true,
    pickupAddress: settings.pickup_address || '',
    enforced: settings.enforce_delivery_areas === true,
    enforcedPrefixes: [...enforcedPrefixes].sort(),
    areas,
  };
}
